import type { FC } from "react";

import { useQuantityLimitHelper } from "@ui/hooks/useQuantityLimitHelper";
import { useTranslations } from "@ui/providers/useTranslations";

import {
  QuantityInput as CoreQuantityInput,
  type QuantityInputProps as CoreQuantityInputProps,
} from "@jti/core-components/components/QuantityInput";

export type QuantityInputProps = Omit<CoreQuantityInputProps, "status" | "helperText"> & {
  orderLimitEnabled?: boolean;
  isOrderLimitReached?: boolean;
  maxOrderLimit?: number;
  currency?: string;
};

/**
 * Application-level wrapper around the design-system `QuantityInput`.
 * Provides the commonly used accessible labels and the order-limit helper text
 * so consumers only pass the quantity-specific props.
 */
export const QuantityInput: FC<QuantityInputProps> = ({
  orderLimitEnabled,
  isOrderLimitReached,
  maxOrderLimit,
  currency,
  disabledAdd = false,
  "aria-label": ariaLabel,
  increaseButtonAriaLabel,
  decreaseButtonAriaLabel,
  ...rest
}) => {
  const { __ } = useTranslations();

  const { status, helperText } = useQuantityLimitHelper({
    orderLimitEnabled,
    isOrderLimitReached,
    disabledAdd,
    maxOrderLimit,
    currency,
  });

  return (
    <CoreQuantityInput
      {...rest}
      aria-label={ariaLabel ?? __("cart.payment_summary.qty")}
      increaseButtonAriaLabel={increaseButtonAriaLabel ?? __("quantityInput.increase")}
      decreaseButtonAriaLabel={decreaseButtonAriaLabel ?? __("quantityInput.decrease")}
      disabledAdd={disabledAdd}
      status={status}
      helperText={helperText}
    />
  );
};
